import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from 'formik';
import MyModal from "../../../../Common/my-modal/my-modal.component";
import CrossIcon from "../../../../icons/cross.icon";
import PlusIcon from "../../../../icons/plus.icon";
import { Integration } from "../add-tool-dropdown/add-tool.dropdown";
import MyDropdown from "../../../../Common/my-dropdown/my-dropdown-new.component";
import ImageLoader from "../../../../Common/ImageLoader";
import integrationUtils from "../../../../../Utils/integration.utils";
import { AddToolDropdownMenu } from "../add-tool-dropdown/add-tool-dropdown.menu";
import DropdownSelector from "../../../../Common/dropdown-selector";
import { PracticeConnectFormValues } from "./practice-connect.types";
import { practiceConnectValidationSchema } from "./practice-connect.validation";
import { DropdownOption } from "../../../../../common/types/app.types";
import useActions from "../../../../../hooks/service-actions.hook";
import { PracticeEffectActions } from "../../../../../common/services/security-stack-view/practice.service";
import { useEntity } from "../../../../../hooks/use-entity.hook";
import { Entities } from "../../../../../common/store/entities/entities.enum";
import useLoaders from "../../../../../hooks/use-loaders.hook";
import { Loaders } from "../../../../../common/store/loaders/loaders.enum";
import { ToolInfoEffectActions } from "../../../../../common/services/security-stack-view/tool-info.service";
import { GetToolInfoResponse } from "../../../../../common/services/security-stack-view/dto/get-tool-info.dto";
import { ToolEffectActions } from "../../../../../common/services/security-stack-view/tool.service";
import { PracticeToolSelected } from "../../../../../common/services/security-stack-view/dto/get-tool-info.dto";
import { Spinner } from "react-bootstrap";
import Suspense from "../../../../Common/suspense.component";
import OrderSummary from "../../../../Common/loader";
import "./practice-connect.modal.scss";

interface PracticeConnectModalProps {
  show: boolean;
  practice?: {
    TechnologyEnum: number;
    IntegrationId: string;
    ToolName: string;
    dummy_SelectedTool_List: any[];
  };
  newTool?: {
    ToolName: string;
    ToolIcon: string;
    obj_IntegrationId: number;
  };
  description?: string;
  loading?: boolean;
  clientId?: number;
  isView?: boolean;
  onHide: () => void;
  refreshData: () => void;
  onOpenUpdateToolModal?: (toolName: string) => void;
}

const PracticeConnectModal: React.FC<PracticeConnectModalProps> = ({
  show,
  clientId,
  isView = false,
  onHide,
  refreshData,
  practice,
  newTool,
  loading,
  onOpenUpdateToolModal,
}) => {
  const { getToolInfo } = useActions(ToolInfoEffectActions);
  const { updateTool } = useActions(ToolEffectActions);
  const { getPracticeToolsById } = useActions(PracticeEffectActions);

  const toolId = practice?.IntegrationId || 0;
  const entityId = clientId ? `${clientId}-${toolId}` : toolId;

  const { entityData: toolInfoData } = useEntity<GetToolInfoResponse>({ entityType: Entities.ToolInfo, entityId: entityId as string });

  const initialValues = useMemo(() => {
    const toolInfoFromList = toolInfoData?.IntegrationInfo_List?.[0];
    const practiceToolSelectedList =( toolInfoData?.PracticeToolSelected_List || [])?.map((item: any) => ({
      IntegrationId: item.ToolId,
      IntegrationName: item.ToolName,
      ToolIcon: item.ToolIcon,
      isCustom: false,
    }));
    
    const selectedTools: any[] = [...practiceToolSelectedList];
    if (newTool) {
      selectedTools.push({
        IntegrationId: newTool.obj_IntegrationId,
        IntegrationName: newTool.ToolName,
        ToolIcon: newTool.ToolIcon,
        isCustom: true,
        isNewSelected: true,
      });
    }
    
    return {
      reason: toolInfoFromList?.Reason || "",
      monitoringOption: toolInfoFromList?.MonitoredBySOC_Selected?.DDLText || toolInfoData?.MonitoredBySOC_List?.[0]?.DDLText || "",
      selectedTools: selectedTools,
    };
  }, [toolInfoData, newTool]);

  const [practiceData, setPracticeData] = useState(initialValues);
  const [searchQuery, setSearchQuery] = useState("");
  
  const { entityData: modalToolsList } = useEntity({
    entityType: Entities.PracticeTools,
    entityId: practice?.IntegrationId,
  }) as { entityData: { Integration_List?: Integration[]; SecurityStack_List?: Integration[] } };
  
  const { isLoading: isModalToolsListLoading } = useLoaders({
    loaders: Loaders.GetPracticeToolsByIdLoader,
  });

  const { isLoading: isUpdatingTool } = useLoaders({ loaders: Loaders.UpdateToolLoader });
  const { isLoading: isToolInfoLoading } = useLoaders({ loaders: Loaders.GetToolInfoLoader });

  const hasSelectedTool = useMemo(() => {
    return practiceData.selectedTools.some((tool: any) => tool.isNewSelected);
  }, [practiceData]);

  const monitoringOptions = useMemo(() => {
    return toolInfoData?.MonitoredBySOC_List?.map((item: any) => ({
      value: item.DDLText,
      label: item.DDLText
    })) || [];
  }, [toolInfoData]);

  const filteredToolsList = useMemo(() => {
    const allTools = [...(modalToolsList?.Integration_List || []), ...(modalToolsList?.SecurityStack_List || [])];

    return allTools
      .filter(item => 
        searchQuery 
          ? item.IntegrationName.toLowerCase().includes(searchQuery.toLowerCase())
          : true
      )
  }, [modalToolsList, practice, searchQuery]);

  const handleFormDataChange = (values: any) => {
    setPracticeData(values);
  }

  const handleConnect = () => {
    const integrationInfo = {
      ...toolInfoData?.IntegrationInfo_List?.[0],
      MonitoredBySOC_Selected: toolInfoData?.MonitoredBySOC_List.find((item: any) => item.DDLText === practiceData.monitoringOption),
      Reason: practiceData.reason,
    };

    const monitoredBySOCSelected = toolInfoData?.MonitoredBySOC_List.find((item: any) => item.DDLText === practiceData.monitoringOption);
    const isManagedBySoc = monitoredBySOCSelected?.DDLText?.toLowerCase().includes('yes') || 
                          monitoredBySOCSelected?.DDLText?.toLowerCase().includes('managed') ||
                          (practiceData.monitoringOption?.toLowerCase().includes('yes') || practiceData.monitoringOption?.toLowerCase().includes('managed'));

    const practiceToolSelectedList: PracticeToolSelected[] = practiceData.selectedTools.map((selectedTool: any) => ({
      ToolId: selectedTool.IntegrationId || 0,
      ToolName: selectedTool.IntegrationName || '',
      ToolIcon: selectedTool.ToolIcon || '',
      IsManagedBySoc: isManagedBySoc || false,
      Enum_IntegrationTechnology: practice?.TechnologyEnum || 0,
    }));

    const practiceToolIdsList = practiceData.selectedTools.map((tool: any) => tool.IntegrationId).filter((id: any) => id);

    updateTool({
      ToolName: practice?.ToolName || '',
      ToolId: toolId,
      IsPractice: true,
      PracticeToolIdsList: practiceToolIdsList,
      PracticeToolSelected_List: practiceToolSelectedList,
      technologyIdsList: toolInfoData?.IntegrationTechnologyType_List?.map((item: any) => item.IntegrationTechnologyTypeEnum) || [],
      technologiesList: toolInfoData?.IntegrationTechnologyType_List?.map((item: any) => ({
        ...item,
        ExistsInTechnologyStack: true
      })) || [],
      IntegrationInfo_List: [integrationInfo],
      PracticeType_List: [],
      clientId,
      callback: () => {
        refreshData();
        onHide();
      }
    });
  }

  const handleContinueWithoutTools = () => {
    const integrationInfo = {
      ...toolInfoData?.IntegrationInfo_List?.[0],
      MonitoredBySOC_Selected: toolInfoData?.MonitoredBySOC_List.find((item: any) => item.DDLText === practiceData.monitoringOption),
      Reason: practiceData.reason,
    }
    updateTool({
      ToolName: practice?.ToolName,
      ToolId: toolId,
      IsPractice: true,
      PracticeToolIdsList: [],
      technologyIdsList: toolInfoData?.IntegrationTechnologyType_List?.map((item: any) => item.IntegrationTechnologyTypeEnum) || [],
      technologiesList: toolInfoData?.IntegrationTechnologyType_List?.map((item: any) => ({
        ...item,
        ExistsInTechnologyStack: true
      })) || [],
      IntegrationInfo_List: [integrationInfo],
      PracticeType_List: [],
      clientId,
      callback: () => {
        refreshData();
        onHide();
      },
    })
  }

  useEffect(() => {
    if (!show) return;
    getPracticeToolsById({ ToolId: toolId });
    getToolInfo({ toolId, clientId });
  }, [toolId, clientId, show]);

  return (
    <MyModal show={show} onHide={onHide} className="practice-connect-modal">
      <MyModal.Header closeButton={true} onCrossClick={onHide} className="border-bottom">
        <MyModal.HeaderTitle className="text-align-center w-100 d-flex align-items-center justify-content-center gap-8">
          {practice?.ToolName}
          {isToolInfoLoading && (
            <Spinner
              animation="border"
              size="sm"
              variant="secondary"
              style={{
                width: "12px",
                height: "12px",
              }}
            />
          )}
        </MyModal.HeaderTitle>
      </MyModal.Header>
      <MyModal.Body className="d-flex flex-column gap-16">
        <Suspense 
          loaders={Loaders.GetToolInfoLoader} 
          fallback={
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '194px' }}>
              <OrderSummary />
            </div>
          }
          forceShow={!!toolInfoData}
        >
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={practiceConnectValidationSchema}
            onSubmit={() => {}}
          >
            {({ values, setFieldValue, isValid, errors, touched }) => (
              <FormComponent 
                values={values}
                setFieldValue={setFieldValue}
                isValid={isValid}
                errors={errors}
                touched={touched}
                monitoringOptions={monitoringOptions}
                practice={practice}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filteredToolsList={filteredToolsList}
                isModalToolsListLoading={isModalToolsListLoading}
                hasSelectedTool={hasSelectedTool}
                onFormDataChange={handleFormDataChange}
                onOpenUpdateToolModal={onOpenUpdateToolModal}
                isView={isView}
              />
            )}
          </Formik>
        </Suspense>
      </MyModal.Body>
      <MyModal.Footer className="d-flex justify-content-end gap-8">
        {!isView && (
          <MyModal.ApplyButton onClick={handleConnect} disabled={isToolInfoLoading || isUpdatingTool}>
            {isUpdatingTool ? (
              <div className="d-flex align-items-center gap-8">
                <Spinner
                  animation="border"
                  size="sm"
                  variant="secondary"
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                />
                <span>Saving...</span>
              </div>
            ) : (
              "Save"
            )}
          </MyModal.ApplyButton>
        )}
      </MyModal.Footer>
    </MyModal>
  );
};

interface FormComponentProps {
  values: PracticeConnectFormValues;
  setFieldValue: (field: string, value: any) => void;
  isValid: boolean;
  errors: any;
  touched: any;
  monitoringOptions: DropdownOption[];
  practice?: any;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredToolsList: any[];
  isModalToolsListLoading: boolean;
  hasSelectedTool: boolean;
  onFormDataChange: (values: any) => void;
  onOpenUpdateToolModal?: (toolName: string) => void;
  isView?: boolean;
}

const FormComponent: React.FC<FormComponentProps> = ({
  values,
  setFieldValue,
  monitoringOptions,
  practice,
  searchQuery,
  setSearchQuery,
  filteredToolsList,
  isModalToolsListLoading,
  onFormDataChange,
  hasSelectedTool,
  onOpenUpdateToolModal,
  isView = false,
}) => {
  const notSelectedToolsList = useMemo(() => {
    return filteredToolsList.filter(item => !values.selectedTools.some(tool => tool.IntegrationId === item.IntegrationId));
  }, [filteredToolsList, values.selectedTools]);
  
  const handleAddNew = () => {
    if (isView) return;
    if (searchQuery && onOpenUpdateToolModal) {
      onOpenUpdateToolModal(searchQuery);
      setSearchQuery('');
    }
    document.body.click();
  };
  
  const handleAddNewIntegration = async (integration: any) => {
    if (isView) return;
    setFieldValue('selectedTools', [...values.selectedTools, {
      ...integration,
      isNewSelected: true,
    }]);
    document.body.click();
  };

  const handleRemoveIntegration = (integration: any) => {
    if (isView) return;
    setFieldValue('selectedTools', values.selectedTools.filter(tool => tool.IntegrationId !== integration.IntegrationId));
  };

  useEffect(() => {
    onFormDataChange(values);
  }, [values]);

  return (
    <Form className="d-flex flex-column gap-16 w-100">
      <div className="update-tool-form__form">
        <fieldset className="update-tool-form__fieldset">
          <legend className="sr-only">Reason</legend>
          <label
            htmlFor="reason"
            className="update-tool-form__label"
          >
            Reason
          </label>
          <div className="update-tool-form__input-wrapper bg-transparent">
            <Field
              type="text"
              id="reason"
              name="reason"
              placeholder="Reason here"
              className="update-tool-form__input bg-transparent"
              aria-label="Reason for connection"
              readOnly={isView}
              disabled={isView}
            />
          </div>
        </fieldset>
        <fieldset className="update-tool-form__fieldset">
          <legend className="sr-only">Monitoring Settings</legend>
          <label
            htmlFor="monitoringOption"
            className="update-tool-form__label"
          >
            Monitored by SOC
          </label>
          <div className="update-tool-form__select-wrapper">
            <DropdownSelector
              list={monitoringOptions}
              selected={{ value: values.monitoringOption, label: values.monitoringOption }}
              onItemSelected={(item) => {
                if (isView) return;
                setFieldValue('monitoringOption', item.value);
              }}
              className="w-100"
              toggleClassName="d-flex align-items-center gap-8 justify-content-between w-100"
              disabled={isView}
            >
              <span className="f-black">
                {values.monitoringOption}
              </span>
              {!isView && (
                <div className="arrow-icon mr-1" />
              )}
            </DropdownSelector>
          </div>
        </fieldset>
      </div>
      <p className="practice-connect-modal__description">
        Do you use a tool for {practice?.ToolName}?
      </p>
      <div className="practice-connect-modal__content">
        {values.selectedTools.map((selectedTool) => (
          <div key={selectedTool.IntegrationId} className="practice-connect-modal__header">
            <div className="practice-connect-modal__header-title-wrapper">
              <ImageLoader
                alt=""
                width={16}
                height={16}
                className="rounded-full"
                style={{ marginLeft: '3px' }}
                src={integrationUtils.getImagePath(selectedTool?.IntegrationId, selectedTool?.ToolIcon)}
                noImageComponent={(
                  <img
                    alt=""
                    width={16}
                    height={16}
                    className="rounded-full"
                    style={{ marginLeft: '3px' }}
                    src={"/images/attack-surface/generic2-dark.svg"}
                  />
                )}
              />
              <h1 className="practice-connect-modal__header-title">
                {selectedTool.IntegrationName}
              </h1>
            </div>

            {!isView && (
              <button
                className="practice-connect-modal__close-button"
                aria-label="Close"
                type="button"
                onClick={() => handleRemoveIntegration(selectedTool)}
              >
                <div className="practice-connect-modal__close-icon-wrapper">
                  <CrossIcon className="practice-connect-modal__close-icon" />
                </div>
              </button>
            )}
          </div>
        ))}

        {!isView && (
          <MyDropdown className="practice-connect-modal__add-tool-dropdown">
            <MyDropdown.Toggle hideArrow>
              <div className="my-button styled justify-content-start d-flex align-items-center f-grey">
                <div className="left-icon">
                  <PlusIcon width="12px" height="12px" fill="#616778" />
                </div>
                <span className="f-14 line-height-22 f-500">Add</span>
              </div>
            </MyDropdown.Toggle>
            <MyDropdown.Menu className="add-tool-dropdown-menu" popperConfig={{ strategy: 'fixed' }} renderOnMount>
              <AddToolDropdownMenu
                toolsList={notSelectedToolsList || []} 
                searchQuery={searchQuery} 
                onSearchChange={setSearchQuery} 
                isLoading={isModalToolsListLoading} 
                onAddTool={handleAddNewIntegration} 
                answerNo={handleAddNew}
                showNoAnswerButton={!!searchQuery}
                showToolsInStack={false}
              />
            </MyDropdown.Menu>
          </MyDropdown>
        )}
      </div>
    </Form>
  );
};

export default PracticeConnectModal;


